import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { UserCheck, X, Loader2, Plus } from 'lucide-react';
import { Button } from '../ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/ged-dossiers/membre`;

export const ExpertsRattaches = ({ membreId, token, userRole }) => {
  const [data, setData] = useState(null);
  const [choix, setChoix] = useState('');
  const [busy, setBusy] = useState('');
  const estSuperAdmin = ['super_admin', 'admin'].includes(String(userRole || '').toLowerCase());
  const headers = { Authorization: `Bearer ${token}` };
  const charger = useCallback(() => {
    axios.get(`${API}/${membreId}/experts`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => { setData(r.data); setChoix(''); }).catch(() => setData({ rattaches: [], disponibles: [] }));
  }, [membreId, token]);
  useEffect(() => { if (membreId && estSuperAdmin) charger(); }, [membreId, estSuperAdmin, charger]);
  if (!membreId || !estSuperAdmin) return null;

  const rattacher = async () => {
    setBusy('add');
    try {
      const r = await axios.post(`${API}/${membreId}/experts`, { expert_id: choix }, { headers });
      toast.success(`${r.data.expert.nom} rattaché${r.data.email_envoye ? ' — email envoyé' : ' (email non envoyé)'}`);
      charger();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Rattachement impossible'); } finally { setBusy(''); }
  };
  const retirer = async (x) => {
    if (!window.confirm(`Retirer ${x.nom} de ce membre ?`)) return;
    setBusy(x.id);
    try { await axios.delete(`${API}/${membreId}/experts/${x.id}`, { headers }); toast.success('Expert retiré'); charger(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Retrait impossible'); } finally { setBusy(''); }
  };

  return (
    <div className="rounded-xl border border-teal-100 bg-gradient-to-br from-teal-50/40 to-white overflow-hidden" data-testid="experts-rattaches">
      <div className="px-4 py-2.5 bg-gradient-to-r from-teal-50 to-emerald-50/40 border-b border-teal-100 flex items-center gap-2">
        <UserCheck className="h-4 w-4 text-teal-700" />
        <span className="text-xs font-bold text-teal-800 uppercase tracking-wider">Experts rattachés</span>
      </div>
      <div className="p-4 space-y-3">
        {data === null ? <Loader2 className="h-4 w-4 animate-spin text-slate-400" /> : (
          <>
            <div className="flex flex-wrap gap-2" data-testid="experts-rattaches-liste">
              {data.rattaches.map((x) => (
                <span key={x.id} className="inline-flex items-center gap-1.5 rounded-full border border-teal-200 bg-white px-3 py-1 text-xs font-semibold text-teal-800" data-testid={`expert-rattache-${x.id}`}>
                  {x.nom}{x.email ? <span className="font-normal text-slate-500">· {x.email}</span> : null}
                  <button type="button" onClick={() => retirer(x)} disabled={busy === x.id} title="Retirer" className="text-slate-400 hover:text-rose-600" data-testid={`expert-retirer-${x.id}`}>
                    {busy === x.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <X className="h-3 w-3" />}
                  </button>
                </span>
              ))}
              {!data.rattaches.length && <p className="text-xs text-slate-400">Aucun expert rattaché.</p>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select value={choix} onChange={(e) => setChoix(e.target.value)} className="min-w-[240px] flex-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs" data-testid="expert-select">
                <option value="">Choisir un expert…</option>
                {data.disponibles.map((x) => <option key={x.id} value={x.id}>{x.nom}{x.email ? ` — ${x.email}` : ''}</option>)}
              </select>
              <Button type="button" size="sm" onClick={rattacher} disabled={!choix || busy === 'add'} className="h-8 bg-teal-700 hover:bg-teal-800 text-xs" data-testid="expert-rattacher-btn">
                {busy === 'add' ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />} Rattacher
              </Button>
            </div>
            <p className="text-[11px] text-slate-500">L'expert reçoit un email et peut ensuite consulter et compléter la GED de ce membre.</p>
          </>
        )}
      </div>
    </div>
  );
};
